import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MONTHLY_PLAN = {
  id: 'monthly',
  name: 'Mensuel',
  label: 'Abonnement Mensuel',
  price: 29.99,
  currency: 'CAD',
  description: 'Accès complet au menu QR AR pour 1 mois',
  features: ['Menu QR illimité', 'Vues AR illimitées', 'Support prioritaire', 'Mises à jour incluses'],
};

// Inner form component (must be inside Elements provider)
const CheckoutForm = ({ clientSecret, planName, amount, paymentType, restaurantName, email, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/restaurant-admin-dashboard`,
          payment_method_data: {
            billing_details: {
              address: {
                country: 'CA',
              },
            },
          },
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(
          error?.type === 'card_error' || error?.type === 'validation_error'
            ? error?.message
            : 'Une erreur est survenue. Veuillez réessayer.'
        );
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        try {
          const { error: confirmError } = await supabase?.functions?.invoke('confirm-payment', {
            body: {
              paymentIntentId: paymentIntent?.id,
              restaurantName,
              email,
              paymentType,
            },
          });
          if (confirmError) {
            console.error('confirm-payment error (non-blocking):', confirmError?.message);
          }
        } catch (confirmErr) {
          console.error('confirm-payment exception (non-blocking):', confirmErr);
        }
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setErrorMessage('Une erreur inattendue est survenue. Veuillez réessayer.');
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="bg-muted/40 rounded-lg p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">{planName}</p>
            <p className="text-xs text-muted-foreground">Paiement sécurisé via Stripe</p>
          </div>
          <p className="text-base font-semibold text-foreground">{amount} CAD</p>
        </div>
        {(() => {
          const base = parseFloat(amount) || 0;
          const tps = base * 0.05;
          const tvq = base * 0.09975;
          const total = base + tps + tvq;
          return (
            <div className="border-t border-border pt-2 space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>TPS (5%)</span>
                <span>{tps?.toFixed(2)} CAD</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>TVQ (9,975%)</span>
                <span>{tvq?.toFixed(2)} CAD</span>
              </div>
              <div className="flex items-center justify-between text-sm font-bold text-foreground border-t border-border pt-1 mt-1">
                <span>Total</span>
                <span>{total?.toFixed(2)} CAD</span>
              </div>
            </div>
          );
        })()}
      </div>
      <PaymentElement
        options={{
          layout: 'tabs',
          fields: { billingDetails: { address: { country: 'never' } } },
        }}
      />
      {errorMessage && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <Icon name="AlertCircle" size={16} color="#ef4444" />
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 py-2.5 border border-border rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              Traitement...
            </>
          ) : (
            <>
              <Icon name="Lock" size={16} />
              Payer {((parseFloat(amount) || 0) * 1.14975)?.toFixed(2)} CAD
            </>
          )}
        </button>
      </div>
      <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
        <Icon name="Shield" size={12} />
        Paiement sécurisé et chiffré par Stripe
      </p>
    </form>
  );
};

// Payment history row
const PaymentHistoryRow = ({ payment }) => {
  const statusConfig = {
    succeeded: { label: 'Réussi', color: 'text-green-600 bg-green-50 border-green-200' },
    pending: { label: 'En attente', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
    failed: { label: 'Échoué', color: 'text-red-600 bg-red-50 border-red-200' },
  };
  const config = statusConfig?.[payment?.payment_status] || statusConfig?.succeeded;
  const date = payment?.created_at
    ? new Date(payment.created_at)?.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="CreditCard" size={14} color="var(--color-primary)" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{payment?.plan_name || '—'}</p>
          <p className="text-xs text-muted-foreground">{date}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <p className="text-sm font-semibold text-foreground">
          {payment?.amount?.toFixed(2)} {payment?.currency?.toUpperCase()}
        </p>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${config?.color}`}>
          {config?.label}
        </span>
      </div>
    </div>
  );
};

// Main PaymentSection component
const PaymentSection = () => {
  const [selectedPlan, setSelectedPlan] = useState(null); // 'monthly' | 'custom'
  const [step, setStep] = useState('plans'); // 'plans' | 'info' | 'checkout' | 'success'
  const [clientSecret, setClientSecret] = useState('');
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [customAmount, setCustomAmount] = useState('');
  const [customAmountError, setCustomAmountError] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    restaurantName: '',
    email: '',
  });

  // Initialize stripePromise inside the component so it always uses the current env key
  const stripePromise = useMemo(() => {
    const key = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!key) return null;
    return loadStripe(key);
  }, []);

  // Detect mode mismatch between publishable key and client_secret
  const modeMismatchWarning = useMemo(() => {
    const key = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY || '';
    if (!clientSecret) return null;
    const keyIsTest = key?.startsWith('pk_test_');
    const keyIsLive = key?.startsWith('pk_live_');
    const secretIsTest = clientSecret?.includes('_test_');
    const secretIsLive = !clientSecret?.includes('_test_');
    if (keyIsTest && secretIsLive) {
      return 'Clé publique en mode TEST mais paiement créé en mode LIVE. Vérifiez que VITE_STRIPE_PUBLISHABLE_KEY et STRIPE_SECRET_KEY appartiennent au même compte et au même mode Stripe.';
    }
    if (keyIsLive && secretIsTest) {
      return 'Clé publique en mode LIVE mais paiement créé en mode TEST. Vérifiez que VITE_STRIPE_PUBLISHABLE_KEY et STRIPE_SECRET_KEY appartiennent au même compte et au même mode Stripe.';
    }
    return null;
  }, [clientSecret]);

  useEffect(() => {
    loadPaymentHistory();
  }, []);

  const loadPaymentHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data, error: fetchError } = await supabase?.from('payments')?.select('*')?.order('created_at', { ascending: false })?.limit(10);
      if (!fetchError) setPaymentHistory(data || []);
    } catch (err) {
      console.error('Failed to load payment history:', err);
    }
    setHistoryLoading(false);
  };

  const handleSelectMonthly = () => {
    setSelectedPlan({ ...MONTHLY_PLAN });
    setStep('info');
    setError('');
    setCustomAmountError('');
  };

  const handleSelectCustom = () => {
    const parsed = parseFloat(customAmount);
    if (!customAmount || isNaN(parsed) || parsed <= 0) {
      setCustomAmountError('Veuillez entrer un montant valide.');
      return;
    }
    setCustomAmountError('');
    setSelectedPlan({
      id: 'custom',
      name: 'Paiement unique',
      label: 'Paiement unique',
      price: parseFloat(parsed?.toFixed(2)),
      currency: 'CAD',
      description: `Paiement unique de ${parsed?.toFixed(2)} CAD`,
    });
    setStep('info');
    setError('');
  };

  const handleInfoSubmit = async (e) => {
    e?.preventDefault();
    if (!customerInfo?.restaurantName || !customerInfo?.email) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount: selectedPlan?.price,
            currency: 'cad',
            planName: selectedPlan?.label,
            description: selectedPlan?.description,
          },
          customerInfo: {
            userId: null,
            firstName: customerInfo?.restaurantName,
            lastName: '',
            email: customerInfo?.email,
            stripeCustomerId: stripeCustomerId || null,
          },
        },
      });

      if (fnError) {
        // Try to parse the actual error message from the edge function body
        let errorMsg = fnError?.message || 'Erreur lors de la création du paiement.';
        try {
          const parsed = typeof fnError?.context?.json === 'function'
            ? await fnError?.context?.json()
            : null;
          if (parsed?.error) errorMsg = parsed?.error;
        } catch (_) {}
        throw new Error(errorMsg);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      setClientSecret(data?.clientSecret);
      if (data?.stripeCustomerId) setStripeCustomerId(data?.stripeCustomerId);
      setStep('checkout');
    } catch (err) {
      const msg = err?.message || '';
      if (msg?.includes('non-2xx') || msg?.includes('Edge Function')) {
        setError('Le service de paiement est temporairement indisponible. Veuillez vérifier que la clé secrète Stripe (STRIPE_SECRET_KEY) est bien configurée dans les secrets Supabase Edge Functions.');
      } else {
        setError(msg || 'Une erreur est survenue. Veuillez réessayer.');
      }
    }

    setIsLoading(false);
  };

  const handlePaymentSuccess = () => {
    setStep('success');
    loadPaymentHistory();
  };

  const handleReset = () => {
    setStep('plans');
    setSelectedPlan(null);
    setClientSecret('');
    setError('');
    setCustomAmount('');
    setCustomAmountError('');
    setCustomerInfo({ restaurantName: '', email: '' });
  };

  return (
    <div className="payment-section space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="CreditCard" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Gérez votre abonnement QR Menu AR</p>
        </div>
      </div>
      {/* Step: Plans */}
      {step === 'plans' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">

            {/* Custom One-Time Payment */}
            <div className="relative bg-card border border-border rounded-xl p-5 transition-all hover:shadow-warm-md hover:border-primary/50">
              <div className="mb-3">
                <h3 className="text-base font-heading font-semibold text-foreground">Paiement unique</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Entrez le montant de votre choix pour un paiement ponctuel</p>
              </div>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e?.target?.value);
                      setCustomAmountError('');
                    }}
                    onClick={(e) => e?.stopPropagation()}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-2xl font-bold border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                  <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">CAD</span>
                </div>
                {customAmountError && (
                  <p className="text-xs text-red-500 mt-1">{customAmountError}</p>
                )}
              </div>
              <ul className="space-y-1.5 mb-4">
                <li className="flex items-center gap-2 text-xs text-foreground">
                  <Icon name="Check" size={12} color="var(--color-primary)" />
                  Montant personnalisé
                </li>
                <li className="flex items-center gap-2 text-xs text-foreground">
                  <Icon name="Check" size={12} color="var(--color-primary)" />
                  Paiement ponctuel, sans engagement
                </li>
                <li className="flex items-center gap-2 text-xs text-foreground">
                  <Icon name="Check" size={12} color="var(--color-primary)" />
                  Paiement sécurisé via Stripe
                </li>
              </ul>
              <button
                onClick={handleSelectCustom}
                className="w-full py-2 rounded-lg text-sm font-medium border border-primary text-primary hover:bg-primary/5 transition-colors"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Step: Customer Info */}
      {step === 'info' && selectedPlan && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-md">
          <button
            onClick={() => setStep('plans')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeft" size={14} />
            Retour aux plans
          </button>
          <h3 className="text-base font-heading font-semibold text-foreground mb-4">
            Vos informations
          </h3>
          <form onSubmit={handleInfoSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Nom du restaurant</label>
              <input
                type="text"
                value={customerInfo?.restaurantName}
                onChange={(e) => setCustomerInfo((p) => ({ ...p, restaurantName: e?.target?.value }))}
                placeholder="Le Bon Goût"
                required
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Courriel</label>
              <input
                type="email"
                value={customerInfo?.email}
                onChange={(e) => setCustomerInfo((p) => ({ ...p, email: e?.target?.value }))}
                placeholder="contact@restaurant.com"
                required
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="AlertCircle" size={14} color="#ef4444" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Chargement...
                </>
              ) : (
                'Continuer vers le paiement'
              )}
            </button>
          </form>
        </div>
      )}
      {/* Step: Checkout */}
      {step === 'checkout' && clientSecret && selectedPlan && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-md">
          <button
            onClick={() => setStep('info')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeft" size={14} />
            Retour
          </button>
          <h3 className="text-base font-heading font-semibold text-foreground mb-4">
            Détails de paiement
          </h3>
          {modeMismatchWarning && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
              <Icon name="AlertTriangle" size={16} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-700">{modeMismatchWarning}</p>
            </div>
          )}
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: { colorPrimary: '#6366f1', borderRadius: '8px' },
              },
            }}
          >
            <CheckoutForm
              clientSecret={clientSecret}
              planName={selectedPlan?.label}
              amount={selectedPlan?.price}
              paymentType={selectedPlan?.id === 'monthly' ? 'monthly' : 'one_time'}
              restaurantName={customerInfo?.restaurantName}
              email={customerInfo?.email}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setStep('plans')}
            />
          </Elements>
        </div>
      )}
      {/* Step: Success */}
      {step === 'success' && (
        <div className="bg-card border border-border rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} color="#16a34a" />
          </div>
          <h3 className="text-lg font-heading font-bold text-foreground mb-2">Paiement réussi!</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Merci pour votre paiement. Votre accès est maintenant actif.
          </p>
          <button
            onClick={handleReset}
            className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Retour aux plans
          </button>
        </div>
      )}
      {/* Payment History */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-heading font-semibold text-foreground">Historique des paiements</h3>
          <button
            onClick={loadPaymentHistory}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
          >
            <Icon name="RefreshCw" size={12} />
            Actualiser
          </button>
        </div>
        {historyLoading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" />
          </div>
        ) : paymentHistory?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Receipt" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-40" />
            <p className="text-sm text-muted-foreground">Aucun paiement pour le moment</p>
          </div>
        ) : (
          <div>
            {paymentHistory?.map((payment) => (
              <PaymentHistoryRow key={payment?.id} payment={payment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSection;
